import { ElementType } from "react";
import { Tag, TagLabel, TagLeftIcon, TagProps } from "@chakra-ui/react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { NA } from "components/Shared/EmptyPlaceholder";

type Props = Omit<TagProps, "children"> & {
  falseIcon?: ElementType;
  falseLabel?: string;
  falseProps?: TagProps;
  trueIcon?: ElementType;
  trueLabel?: string;
  trueProps?: TagProps;
  value: boolean | null | undefined;
};

export default function BooleanTag({
  falseIcon = CloseIcon,
  falseLabel = "No",
  falseProps,
  trueIcon = CheckIcon,
  trueLabel = "Yes",
  trueProps,
  value,
  ...rest
}: Props) {
  if (typeof value !== "boolean") return <NA />;
  return (
    <Tag
      {...rest}
      data-checked={value || undefined}
      // eslint-disable-next-line no-underscore-dangle
      _checked={{ ...trueProps, ...rest._checked }}
      sx={{
        ...(falseProps ? { "&:not([data-checked])": falseProps } : {}),
        ...rest.sx,
        "&[data-checked] > svg": { boxSize: ".9em" },
      }}
    >
      <TagLeftIcon
        className="boolean-tag-icon"
        as={value ? trueIcon : falseIcon}
        boxSize=".75em"
      />
      <TagLabel>{value ? trueLabel : falseLabel}</TagLabel>
    </Tag>
  );
}

export const GREEN_PROPS = {
  backgroundColor: "green.100",
  borderColor: "green.400",
  color: "green.600",
};

export const RED_PROPS = {
  backgroundColor: "red.100",
  borderColor: "red.300",
  color: "red.600",
};

export const GRAY_PROPS = {
  backgroundColor: "gray.100",
  borderColor: "gray.400",
  color: "gray.600",
};

type ColorfulBooleanTagProps = Props & {
  isColorInverse?: boolean;
};

export function ColorfulBooleanTag({
  falseProps,
  isColorInverse,
  trueProps,
  ...rest
}: ColorfulBooleanTagProps) {
  return (
    <BooleanTag
      borderWidth={1}
      {...rest}
      trueProps={{
        ...(isColorInverse ? RED_PROPS : GREEN_PROPS),
        ...trueProps,
      }}
      falseProps={{
        ...(isColorInverse ? GREEN_PROPS : RED_PROPS),
        ...falseProps,
      }}
    />
  );
}
